import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
  alpha,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import { Line } from 'react-chartjs-2';
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip
} from 'chart.js';
import { APIService } from 'src/api/api';
import { getFormattedDateTime12Hour, getFuelQuantity } from 'src/utils/getInitials';

ChartJS.register(CategoryScale, LinearScale, LineElement, PointElement, Filler, Tooltip, Legend);

const RANGE_OPTIONS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: '7D' },
  { id: '30d', label: '30D' }
];

const toUnixSeconds = (date) => Math.floor(date.getTime() / 1000);

const getRange = (rangeId) => {
  const now = new Date();
  const todayStart = new Date(now);
  todayStart.setHours(0, 0, 0, 0);

  if (rangeId === 'yesterday') {
    const yesterdayStart = new Date(todayStart);
    yesterdayStart.setDate(yesterdayStart.getDate() - 1);
    return {
      from: toUnixSeconds(yesterdayStart),
      to: toUnixSeconds(todayStart)
    };
  }

  if (rangeId === '7d') {
    const start = new Date(now);
    start.setDate(start.getDate() - 7);
    return {
      from: toUnixSeconds(start),
      to: toUnixSeconds(now)
    };
  }

  if (rangeId === '30d') {
    const start = new Date(now);
    start.setDate(start.getDate() - 30);
    return {
      from: toUnixSeconds(start),
      to: toUnixSeconds(now)
    };
  }

  return {
    from: toUnixSeconds(todayStart),
    to: toUnixSeconds(now)
  };
};

const getRootResult = (response) => response?.result || response || {};

const normalizeSeries = (series) => (Array.isArray(series) ? series : [])
  .map((point) => {
    if (!Array.isArray(point) || point.length < 2) return null;
    const timestamp = Number(point[0]);
    const value = Number(point[1]);
    if (!Number.isFinite(timestamp) || !Number.isFinite(value)) return null;
    return {
      timestamp,
      value: Number(value.toFixed(2))
    };
  })
  .filter(Boolean);

const getFuelLevelEntry = (fuelLevelResponse) => (
  fuelLevelResponse?.result?.results?.data?.[0]
  || fuelLevelResponse?.results?.data?.[0]
  || fuelLevelResponse?.data?.[0]
  || null
);

const normalizeFuelLevel = (fuelLevelResponse) => {
  const entry = getFuelLevelEntry(fuelLevelResponse);
  if (!entry || typeof entry !== 'object') {
    return {
      points: [],
      actualPoints: [],
      smoothedPoints: [],
      tankCapacity: null,
      tankNumber: null
    };
  }

  const smoothedPoints = normalizeSeries(entry.approxValues);
  const actualPoints = normalizeSeries(entry.rawValues);

  return {
    points: smoothedPoints.length ? smoothedPoints : actualPoints,
    actualPoints,
    smoothedPoints,
    tankCapacity: Number.isFinite(Number(entry.tankCapacity)) ? Number(entry.tankCapacity) : null,
    tankNumber: Number.isFinite(Number(entry.tankNumber)) ? Number(entry.tankNumber) : null
  };
};

const isTrackPointLike = (point) => {
  if (Array.isArray(point)) return point.length >= 2;
  return Boolean(point && typeof point === 'object');
};

const getTrackEntry = (trackFuelResponse) => {
  const roots = [
    trackFuelResponse?.result?.data,
    trackFuelResponse?.result?.results?.data?.[0],
    trackFuelResponse?.data,
    trackFuelResponse?.result,
    trackFuelResponse
  ].filter(Boolean);

  return roots.find((root) => (
    root
    && typeof root === 'object'
    && (
      Array.isArray(root.track)
      || Array.isArray(root.trackPoints)
      || Array.isArray(root.points)
      || Array.isArray(root.routePoints)
      || (Array.isArray(root.data) && root.data.some(isTrackPointLike))
    )
  )) || null;
};

const normalizeTrackSummary = (trackFuelResponse) => {
  const entry = getTrackEntry(trackFuelResponse);
  const rawPoints = entry?.track || entry?.trackPoints || entry?.points || entry?.routePoints || entry?.data || [];
  const points = (Array.isArray(rawPoints) ? rawPoints : []).filter(isTrackPointLike);
  const lastPoint = points[points.length - 1] || null;

  let latitude = null;
  let longitude = null;
  if (Array.isArray(lastPoint)) {
    longitude = Number(lastPoint[0]);
    latitude = Number(lastPoint[1]);
  } else if (lastPoint) {
    latitude = Number(lastPoint.latitude ?? lastPoint.lat ?? lastPoint.lt);
    longitude = Number(lastPoint.longitude ?? lastPoint.lng ?? lastPoint.lon ?? lastPoint.lg);
  }

  return {
    pointCount: points.length,
    latitude: Number.isFinite(latitude) ? latitude : null,
    longitude: Number.isFinite(longitude) ? longitude : null
  };
};

const getSeriesStats = (points) => {
  if (!points.length) {
    return {
      initial: 0,
      final: 0,
      minimum: 0,
      maximum: 0
    };
  }
  const values = points.map((point) => point.value);
  return {
    initial: values[0],
    final: values[values.length - 1],
    minimum: Math.min(...values),
    maximum: Math.max(...values)
  };
};

const SummaryPill = ({ label, value }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1,
        minWidth: { xs: 'calc(50% - 6px)', sm: 132 },
        border: '1px solid',
        borderColor: alpha(theme.palette.divider, 0.8),
        borderRadius: 1.5,
        bgcolor: '#F8FAFC'
      }}
    >
      <Typography variant="caption" color="text.secondary" fontWeight={700}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={800} color="text.primary" sx={{ mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
};

SummaryPill.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired
};

const TankFuelGraph = ({ tank }) => {
  const theme = useTheme();
  const [rangeId, setRangeId] = useState('today');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [graphData, setGraphData] = useState(null);
  const [expanded, setExpanded] = useState(false);

  const isEnabled = Boolean(tank?.fuel_graph_enabled);
  const isTrackFuel = tank?.fuel_graph_mode === 'track_fuel';

  const fetchGraph = useCallback(async () => {
    if (!tank?.id || !isEnabled) return;
    const range = getRange(rangeId);
    setLoading(true);
    setError('');
    try {
      const response = await APIService.getTankFuelGraph(tank.id, {
        timeBegin: range.from,
        timeEnd: range.to,
        maxPoints: 400
      });
      if (!response?.success) throw response;
      const result = getRootResult(response);
      setGraphData({
        mode: result.mode,
        fuelLevel: normalizeFuelLevel(result.fuel_level),
        trackSummary: normalizeTrackSummary(result.track_fuel)
      });
    } catch (requestError) {
      setGraphData(null);
      setError(requestError?.data?.error_message || requestError?.error_message || 'Unable to load tank fuel graph.');
    } finally {
      setLoading(false);
    }
  }, [isEnabled, rangeId, tank?.id]);

  useEffect(() => {
    fetchGraph();
  }, [fetchGraph]);

  const fuelLevel = graphData?.fuelLevel || {};
  const points = fuelLevel.points || [];
  const actualPoints = fuelLevel.actualPoints || [];
  const smoothedPoints = fuelLevel.smoothedPoints || [];
  const trackSummary = graphData?.trackSummary || null;
  const hasTrackPosition = Boolean(
    trackSummary
    && trackSummary.latitude !== null
    && trackSummary.longitude !== null
  );

  const stats = useMemo(() => getSeriesStats(points), [points]);
  const chartTimestamps = useMemo(() => (
    [...new Set([...smoothedPoints, ...actualPoints].map((point) => point.timestamp))]
      .sort((firstTimestamp, secondTimestamp) => firstTimestamp - secondTimestamp)
  ), [actualPoints, smoothedPoints]);

  const chartData = useMemo(() => {
    const smoothedMap = new Map(smoothedPoints.map((point) => [point.timestamp, point.value]));
    const actualMap = new Map(actualPoints.map((point) => [point.timestamp, point.value]));
    const labels = chartTimestamps.map((timestamp) => getFormattedDateTime12Hour(timestamp, false));
    const datasets = [];

    if (smoothedPoints.length) {
      datasets.push({
        label: 'Smooth fuel',
        data: chartTimestamps.map((timestamp) => smoothedMap.get(timestamp) ?? null),
        borderColor: '#0F172A',
        backgroundColor: 'rgba(15, 23, 42, 0.08)',
        tension: 0.25,
        pointRadius: 0,
        borderWidth: 3,
        fill: true,
        spanGaps: true
      });
    }

    if (actualPoints.length) {
      datasets.push({
        label: 'Actual fuel',
        data: chartTimestamps.map((timestamp) => actualMap.get(timestamp) ?? null),
        borderColor: '#F97316',
        backgroundColor: 'rgba(249, 115, 22, 0.08)',
        tension: 0,
        pointRadius: chartTimestamps.length > 80 ? 0 : 1.5,
        borderWidth: 1.8,
        fill: false,
        spanGaps: true
      });
    }

    if (!datasets.length && points.length) {
      datasets.push({
        label: 'Fuel volume',
        data: points.map((point) => point.value),
        borderColor: '#2563EB',
        backgroundColor: 'rgba(37, 99, 235, 0.12)',
        tension: 0.3,
        pointRadius: points.length > 80 ? 0 : 2,
        fill: true,
        spanGaps: true
      });
      return {
        labels: points.map((point) => getFormattedDateTime12Hour(point.timestamp, false)),
        datasets
      };
    }

    return {
      labels,
      datasets
    };
  }, [actualPoints, chartTimestamps, points, smoothedPoints]);

  const summaryItems = [
    { label: 'Initial', value: getFuelQuantity(stats.initial) },
    { label: 'Final', value: getFuelQuantity(stats.final) },
    { label: 'Minimum', value: getFuelQuantity(stats.minimum) },
    {
      label: isTrackFuel ? 'Track Points' : 'Maximum',
      value: isTrackFuel ? String(trackSummary?.pointCount || 0) : getFuelQuantity(stats.maximum)
    }
  ];

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index'
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        align: 'start',
        labels: {
          boxWidth: 18,
          boxHeight: 8,
          usePointStyle: true,
          color: '#475569',
          font: {
            size: 12,
            weight: '700'
          }
        }
      },
      tooltip: {
        backgroundColor: 'rgba(15, 23, 42, 0.94)',
        borderWidth: 0,
        callbacks: {
          label: (context) => `${context.dataset.label}: ${Number(context.parsed.y || 0).toFixed(1)} L`
        }
      }
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 7,
          color: '#64748B'
        },
        grid: {
          display: false
        }
      },
      y: {
        ticks: {
          color: '#64748B',
          callback: (value) => `${value}`
        },
        title: {
          display: true,
          text: 'Fuel (L)',
          color: '#475569',
          font: {
            weight: '700'
          }
        },
        grid: {
          color: 'rgba(148, 163, 184, 0.18)'
        }
      }
    }
  }), []);

  let chartContent = (
    <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={1}>
      <LocalGasStationIcon sx={{ color: 'text.disabled' }} />
      <Typography variant="body2" color="text.secondary">
        No fuel level data found for this range.
      </Typography>
    </Stack>
  );
  if (loading) {
    chartContent = (
      <Stack alignItems="center" justifyContent="center" sx={{ height: '100%' }} spacing={1}>
        <CircularProgress size={28} />
        <Typography variant="body2" color="text.secondary">
          Loading fuel graph
        </Typography>
      </Stack>
    );
  } else if (points.length || actualPoints.length || smoothedPoints.length) {
    chartContent = <Line data={chartData} options={chartOptions} />;
  }

  if (!isEnabled) {
    return (
      <Alert severity="info">
        Fuel graph is available after enabling a supported fleet provider and storing the tank unit id.
      </Alert>
    );
  }

  return (
    <Stack spacing={2}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="h6" fontWeight={800}>
            Fuel Graph
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isTrackFuel ? 'Track + fuel for movable tank' : 'Fuel level for stationary tank'}
          </Typography>
        </Box>
        <ButtonGroup size="small" variant="outlined">
          {RANGE_OPTIONS.map((option) => (
            <Button
              key={option.id}
              variant={rangeId === option.id ? 'contained' : 'outlined'}
              onClick={() => setRangeId(option.id)}
              sx={{ textTransform: 'none', fontWeight: 700 }}
            >
              {option.label}
            </Button>
          ))}
        </ButtonGroup>
      </Stack>

      {error && <Alert severity="error">{error}</Alert>}

      <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
        {summaryItems.map((item) => (
          <SummaryPill key={item.label} label={item.label} value={item.value} />
        ))}
      </Stack>

      <Box
        sx={{
          height: { xs: 480, md: 620 },
          p: { xs: 1.5, md: 2.5 },
          border: '1px solid',
          borderColor: theme.palette.divider,
          borderRadius: 1.5,
          bgcolor: '#FFFFFF',
          position: 'relative'
        }}
      >
        <IconButton
          aria-label="Expand fuel graph"
          size="small"
          onClick={() => setExpanded(true)}
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 2,
            bgcolor: '#FFFFFF',
            border: '1px solid',
            borderColor: theme.palette.divider,
            '&:hover': {
              bgcolor: '#F8FAFC'
            }
          }}
        >
          <OpenInFullIcon fontSize="small" />
        </IconButton>
        {chartContent}
      </Box>

      {isTrackFuel && hasTrackPosition && (
        <Alert severity="info">
          Last track point:
          {' '}
          {trackSummary.latitude.toFixed(6)}
          ,
          {' '}
          {trackSummary.longitude.toFixed(6)}
        </Alert>
      )}

      <Dialog
        fullScreen
        open={expanded}
        onClose={() => setExpanded(false)}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={800}>
              Fuel Graph
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {tank?.name || 'Tank'}
              {' '}
              |
              {' '}
              {isTrackFuel ? 'Track + fuel' : 'Fuel level'}
            </Typography>
          </Box>
          <IconButton aria-label="Close" onClick={() => setExpanded(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, bgcolor: '#F8FAFC' }}>
          <Stack spacing={2} sx={{ height: '100%' }}>
            <Stack direction="row" spacing={1.25} flexWrap="wrap" useFlexGap>
              {summaryItems.map((item) => (
                <SummaryPill key={item.label} label={item.label} value={item.value} />
              ))}
            </Stack>
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                p: 2.5,
                border: '1px solid',
                borderColor: theme.palette.divider,
                borderRadius: 1.5,
                bgcolor: '#FFFFFF',
                position: 'relative'
              }}
            >
              {chartContent}
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

TankFuelGraph.propTypes = {
  tank: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    fuel_graph_enabled: PropTypes.bool,
    fuel_graph_mode: PropTypes.string
  })
};

export default TankFuelGraph;
